import { Send, Radar, ShieldCheck, Layers, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useOpenSandbox, useOpenHarmony } from "@/contexts/ModalContext";

const steps = [
  {
    icon: Send,
    title: "Create Booking",
    endpoint: "POST /v1/booking/create",
    description: "Platform posts booking metadata with a hashed customer identifier. ClearSlot computes deterministic advisory signals against the network's event history.",
    footer: "Non-blocking. Stateless. Idempotent.",
  },
  {
    icon: Radar,
    title: "Score Resolution",
    endpoint: "GET /v1/score/{customer_hash}",
    description: "Returns an advisory score (0–100), active signals, behavioural pattern, and recommended action. Platform decides what to do with the response.",
    footer: "Advisory only. No enforcement on our side.",
  },
  {
    icon: ShieldCheck,
    title: "Event Feedback (Optional)",
    endpoint: "POST /v1/event/no-show",
    description: "Report the booking outcome — no-show, late cancel, or completion. The signal updates the guest's score across the network.",
    footer: "Signals expire on a defined TTL.",
  },
];

const hashSpec = [
  { label: "Field", value: "customer_hash" },
  { label: "Format", value: "Phone number in E.164 (e.g. +4570123456)" },
  { label: "Pre-processing", value: "None — send the raw value" },
  { label: "Server handling", value: "Hashed on receipt; raw value never persisted" },
  { label: "In transit", value: "HTTPS only" },
  { label: "Determinism", value: "Same input → same stored identifier across the network" },
];

const HowItWorks = () => {
  
  const { setOpen: setSandboxOpen } = useOpenSandbox();
  const { setOpen: setHarmonyOpen } = useOpenHarmony();

  return (
    <section id="how-it-works" className="relative py-16 overflow-hidden bg-muted/30">
      <div className="absolute inset-0 bg-grid opacity-[0.13]" />

      <div className="container relative mx-auto px-6">
        {/* Header */}
        <div className="mb-12 flex flex-col items-center text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary">
            <Layers className="h-4 w-4" />
            Integration
          </div>
          <h2 className="mb-4 text-3xl font-bold text-foreground md:text-4xl lg:text-5xl">
            Three calls. Stateless. Fail-open.{" "}
            <span className="text-primary">Live in a sprint.</span>
          </h2>
          <p className="mx-auto max-w-xl text-lg text-muted-foreground">
            No session, no persistent connection, no SDK required. If ClearSlot is unreachable, the booking proceeds.
          </p>
        </div>

        {/* Mobile: accordion */}
        <Accordion
          type="single"
          collapsible
          defaultValue={steps[0].title}
          className="md:hidden space-y-3"
        >
          {steps.map((step) => (
            <AccordionItem
              key={step.title}
              value={step.title}
              className="rounded-xl border border-border bg-card px-4"
            >
              <AccordionTrigger className="hover:no-underline py-4">
                <div className="flex items-center gap-3 flex-1 text-left">
                  <step.icon className="h-5 w-5 text-muted-foreground shrink-0" />
                  <h3 className="text-base font-semibold text-foreground leading-tight">
                    {step.title}
                  </h3>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-3 pb-4">
                  <div className="font-mono text-sm text-foreground/80 break-all">{step.endpoint}</div>
                  <p className="text-sm text-muted-foreground">{step.description}</p>
                  <p className="font-mono text-xs text-muted-foreground/70">{step.footer}</p>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* Desktop: grid */}
        <div className="hidden md:grid max-w-4xl mx-auto grid-cols-1 md:grid-cols-3 gap-6">
          {steps.map((step, index) => (
            <div
              key={index}
              className="bg-card border border-border rounded-xl px-6 py-6 flex flex-col"
            >
              <step.icon className="h-5 w-5 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-3">{step.title}</h3>
              <div className="mb-3 font-mono text-sm text-foreground/80 break-all overflow-x-auto max-w-full">{step.endpoint}</div>
              <p className="text-sm text-muted-foreground mb-4 flex-1">{step.description}</p>
              <p className="font-mono text-xs text-muted-foreground/70">{step.footer}</p>
            </div>
          ))}
        </div>

        {/* Advisory statement */}
        <p className="mt-10 text-center text-sm text-muted-foreground max-w-2xl mx-auto">
          <span className="font-medium text-foreground">The platform retains full control of the booking flow.</span>{" "}
          ClearSlot does not block, redirect, or modify bookings. If the API is unreachable, the booking proceeds.
        </p>

        {/* Spec row */}
        <div className="mt-6 text-center font-mono text-xs text-muted-foreground/70">
          Latency: &lt;50ms p95 · Availability: 99.9% · Isolation: sandbox | live · Identifier: hashed server-side · Contract: fail-open
        </div>

        {/* Hash Specification */}
        <Collapsible className="mt-10 max-w-2xl mx-auto">
          <CollapsibleTrigger className="flex items-center justify-center gap-2 w-full group cursor-pointer">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Hash Specification
            </p>
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-4">
            <div className="space-y-1.5">
              {hashSpec.map((row, i) => (
                <div key={i} className="flex items-baseline gap-3 text-xs">
                  <span className="font-mono text-muted-foreground/70 w-28 shrink-0 text-right">{row.label}</span>
                  <span className="font-mono text-foreground/70">{row.value}</span>
                </div>
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>


        {/* CTA strip */}
        <div className="mt-16 flex flex-col items-center gap-4">
          <p className="text-sm text-muted-foreground text-center max-w-md">
            Watch the coordination layer handle four real-world booking scenarios.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="text-sm min-h-[44px] md:min-h-0" onClick={() => setHarmonyOpen(true)}>
              See it in action
            </Button>
            <Button variant="outline" className="text-sm min-h-[44px] md:min-h-0" onClick={() => setSandboxOpen(true)}>
              Try the Sandbox
            </Button>
          </div>
        </div>
      </div>

    </section>
  );
};

export default HowItWorks;
